import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useClient } from 'urql';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  Chain,
  MainWalletPurpose,
  MintSurface,
  useGetMintsQuery,
  useGetWithdrawalSwapSourceMintsQuery,
  useWithdrawalSwapDestinationsQuery,
  useWithdrawalSwapQuoteQuery,
  WithdrawalSwapQuoteDocument,
  WithdrawalSwapQuoteQuery,
  WithdrawalSwapQuoteQueryVariables,
  useGetWalletsQuery,
  useGetWhitelistQuery,
  useWithdrawableBalanceQuery,
  useWithdrawMutation,
} from 'src/generated/graphql';
import { paths } from 'src/routes/paths';
import { withdrawalSwapErrorMessage } from 'src/components/withdrawal-swap/errors';
import { WithdrawalSwapQuotePanel } from 'src/components/withdrawal-swap/quote-panel';
import { humanToBaseUnits } from 'src/pages/dashboard/swap/utils';
import { mintTokenIconUrl } from 'src/utils/utilsCrypto';
import {
  NETWORK_PILL_ORDER,
  networkDisplayName,
} from './purpose-wallet-helpers';
import { WithdrawTotpDialog } from './withdraw-totp-dialog';

type WithdrawTokenOption = {
  mintId: number;
  chain: string;
  symbol: string;
  name: string;
  decimals: number;
  usdPrice: string;
  img: string | null | undefined;
  isTestnet: boolean;
  balance: string;
  usdBalance: number;
};

type ChainFilter = 'all' | string;

/**
 * Exact atomic amount for a human decimal string, or null unless it is a
 * non-negative decimal with at most `decimals` fraction digits.
 */
const toAtomicAmount = (human: string, decimals: number): string | null => {
  const fraction = human.trim().split('.')[1] ?? '';
  return fraction.length > decimals ? null : humanToBaseUnits(human, decimals);
};

const QUOTE_REFRESH_MS = 30_000;
const QUOTE_DEBOUNCE_MS = 600;
const DEFAULT_SLIPPAGE_BPS = 100;
const MIN_SLIPPAGE_BPS = 10;
const MAX_SLIPPAGE_BPS = 500;

const fmtUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    amount,
  );

/** Human crypto string from a USD amount and mint price (max `decimals` places). */
const cryptoHumanFromUsd = (
  usdHuman: string,
  usdPrice: number,
  decimals: number,
): string => {
  const usd = parseFloat(usdHuman);
  if (!Number.isFinite(usd) || usd <= 0 || usdPrice <= 0) {
    return '0';
  }
  const crypto = usd / usdPrice;
  const fixed = crypto.toFixed(decimals);
  return fixed.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
};

type WithdrawAmountMode = 'crypto' | 'usd';

export type WithdrawFundsDialogProps = {
  organizationId: string;
  open: boolean;
  onClose: () => void;
  /** Which wallet purpose to withdraw from */
  purpose?: MainWalletPurpose;
  /** After a successful request (refresh balances) */
  onSuccess?: () => void;
};

export function WithdrawFundsDialog({
  organizationId,
  open,
  onClose,
  purpose = MainWalletPurpose.Default,
  onSuccess,
}: WithdrawFundsDialogProps) {
  const [chain, setChain] = useState('');
  const [mintId, setMintId] = useState(0);
  const [chainFilter, setChainFilter] = useState<ChainFilter>('all');
  const [whithdrawAddressId, setWithdrawAddressId] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState('0');
  const [withdrawAmountUsd, setWithdrawAmountUsd] = useState('0');
  const [amountMode, setAmountMode] = useState<WithdrawAmountMode>('crypto');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [totpDialogOpen, setTotpDialogOpen] = useState(false);
  // 'same' = plain withdraw; 'other' = receive as another asset (swap).
  const [receiveAs, setReceiveAs] = useState<'same' | 'other'>('same');
  const [destinationChain, setDestinationChain] = useState<Chain | ''>('');
  const [destinationAssetId, setDestinationAssetId] = useState<string | null>(null);
  const [slippageBps, setSlippageBps] = useState(String(DEFAULT_SLIPPAGE_BPS));
  const [debouncedAmountAtomic, setDebouncedAmountAtomic] = useState<string | null>(null);
  const [debouncedSlippageBps, setDebouncedSlippageBps] = useState(String(DEFAULT_SLIPPAGE_BPS));
  const [refreshedQuote, setRefreshedQuote] = useState<{
    key: string;
    quote: WithdrawalSwapQuoteQuery['withdrawalSwapQuote'];
  } | null>(null);
  const [quoteNotice, setQuoteNotice] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const client = useClient();

  const [
    { data: getOrganizationWalletsData },
    getOrganizationWallets,
  ] = useGetWalletsQuery({
    variables: { organizationId, purpose },
    pause: !open,
  });
  const organizationWallets = getOrganizationWalletsData?.getWallets;

  const [{ data: getMintsData }] = useGetMintsQuery({
    variables: {
      organizationId,
      surface:
        purpose === MainWalletPurpose.Default
          ? MintSurface.Default
          : MintSurface.Platform,
    },
    pause: !open || !organizationId,
  });
  const mints = getMintsData?.getMints;

  // Not in the shared Mint fragment; null is treated as false.
  const [{ data: swapSourceData }] = useGetWithdrawalSwapSourceMintsQuery({
    variables: {
      organizationId,
      surface:
        purpose === MainWalletPurpose.Default
          ? MintSurface.Default
          : MintSurface.Platform,
    },
    pause: !open || !organizationId,
  });

  const withdrawOptions = useMemo((): WithdrawTokenOption[] => {
    const rows: WithdrawTokenOption[] = [];
    for (const wallet of organizationWallets ?? []) {
      for (const asset of wallet.assets) {
        const balanceNum = Number(asset.balance);
        if (!Number.isFinite(balanceNum) || balanceNum <= 0) {
          continue;
        }
        const usdFromAsset = Number(asset.usdBalance);
        const price = Number(asset.mint.usdPrice);
        const usdBalance =
          Number.isFinite(usdFromAsset) && usdFromAsset > 0
            ? usdFromAsset
            : Number.isFinite(price) && price > 0
              ? balanceNum * price
              : 0;
        rows.push({
          mintId: asset.mint.id,
          chain: asset.mint.chain,
          symbol: asset.mint.symbol,
          name: asset.mint.name,
          decimals: asset.mint.decimals,
          usdPrice: asset.mint.usdPrice,
          img: asset.mint.img,
          isTestnet: asset.mint.isTestnet,
          balance: asset.balance,
          usdBalance,
        });
      }
    }
    rows.sort((a, b) => b.usdBalance - a.usdBalance);
    return rows;
  }, [organizationWallets]);

  const networkPills = useMemo(() => {
    const present = new Set(withdrawOptions.map((o) => o.chain));
    return NETWORK_PILL_ORDER.filter((c) => present.has(c));
  }, [withdrawOptions]);

  const filteredWithdrawOptions = useMemo(
    () =>
      chainFilter === 'all'
        ? withdrawOptions
        : withdrawOptions.filter((o) => o.chain === chainFilter),
    [withdrawOptions, chainFilter],
  );

  const selectWithdrawToken = useCallback((option: WithdrawTokenOption) => {
    setMintId(option.mintId);
    setChain(option.chain);
    setWithdrawAmount('0');
    setWithdrawAmountUsd('0');
    setDestinationAssetId(null);
    setReceiveAs('same');
    setDestinationChain('');
  }, []);

  // Drop a network pill that no longer has any positive balances.
  useEffect(() => {
    if (chainFilter !== 'all' && !networkPills.includes(chainFilter as (typeof NETWORK_PILL_ORDER)[number])) {
      setChainFilter('all');
    }
  }, [chainFilter, networkPills]);

  // Keep mint/chain on a row that still has a balance in the active filter.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (filteredWithdrawOptions.length === 0) {
      setMintId(0);
      setChain('');
      return;
    }
    if (!filteredWithdrawOptions.some((o) => o.mintId === mintId)) {
      selectWithdrawToken(filteredWithdrawOptions[0]);
    }
  }, [open, filteredWithdrawOptions, mintId, selectWithdrawToken]);

  const swapChainOptions = useMemo(
    () => Array.from(new Set((mints ?? []).map((m) => m.chain))),
    [mints]
  );
  const isSwapSource =
    swapSourceData?.getMints.find((m) => m.id === mintId)?.withdrawalSwapSource === true;
  // The mode only applies while the source mint can be swapped.
  const isSwap = receiveAs === 'other' && isSwapSource;

  const [{ data: destinationsData, fetching: destinationsFetching }] =
    useWithdrawalSwapDestinationsQuery({
      variables: { organizationId, chain: destinationChain as Chain },
      pause: !open || !organizationId || !isSwap || !destinationChain,
    });
  const destinationOptions = useMemo(
    () =>
      destinationChain
        ? (destinationsData?.withdrawalSwapDestinations ?? []).filter(
            // The same Cheddar mint is not a swap.
            (d) => d.cheddarMintId !== mintId
          )
        : [],
    [destinationsData, destinationChain, mintId]
  );
  const selectedDestination =
    destinationOptions.find((d) => d.assetId === destinationAssetId) ?? null;

  const [{ data: getOrganizationWhitelistData }] = useGetWhitelistQuery({
    variables: { organizationId },
    pause: !open,
  });
  const organizationWhitelist = getOrganizationWhitelistData?.getWhitelist;
  // Memoized so the selection effect below runs only when the whitelist data or
  // chain changes, not on every render (a fresh array each render reset the pick).
  const whitelistAddressByChain = useMemo(
    () =>
      organizationWhitelist?.filter(
        (address) => address.chain === (isSwap ? destinationChain : chain)
      ),
    [organizationWhitelist, chain, isSwap, destinationChain]
  );

  useEffect(() => {
    if (!whitelistAddressByChain || whitelistAddressByChain.length === 0) {
      setWithdrawAddressId(0);
      return;
    }
    // Keep the user's pick while it is still an address on this chain.
    setWithdrawAddressId((current) =>
      whitelistAddressByChain.some((address) => address.id === current)
        ? current
        : whitelistAddressByChain[0].id
    );
  }, [whitelistAddressByChain]);

  const [, withdraw] = useWithdrawMutation();

  const handleOpenRefresh = useCallback(() => {
    if (open) {
      void getOrganizationWallets({ requestPolicy: 'network-only' });
    }
  }, [getOrganizationWallets, open]);

  useEffect(() => {
    handleOpenRefresh();
  }, [handleOpenRefresh]);

  useEffect(() => {
    if (!open) {
      setTotpDialogOpen(false);
      setWithdrawAmount('0');
      setWithdrawAmountUsd('0');
      setAmountMode('crypto');
      setChainFilter('all');
      setMintId(0);
      setChain('');
    }
  }, [open]);

  useEffect(() => {
    setTotpDialogOpen(false);
  }, [mintId, whithdrawAddressId, withdrawAmount, destinationAssetId, slippageBps, receiveAs]);

  const selectedOption = useMemo(
    () => withdrawOptions.find((o) => o.mintId === mintId) ?? null,
    [withdrawOptions, mintId],
  );
  // Prefer the balance-row mint; fall back to getMints for decimals/price.
  const selectedMint = useMemo(() => {
    if (selectedOption) {
      return {
        id: selectedOption.mintId,
        symbol: selectedOption.symbol,
        decimals: selectedOption.decimals,
        usdPrice: selectedOption.usdPrice,
        chain: selectedOption.chain,
        name: selectedOption.name,
        img: selectedOption.img,
        isTestnet: selectedOption.isTestnet,
      };
    }
    return mints?.find((mint) => mint.id === mintId) ?? null;
  }, [selectedOption, mints, mintId]);
  const mintSymbol = selectedMint?.symbol;
  const mintUsdPrice = selectedMint
    ? parseFloat(selectedMint.usdPrice)
    : NaN;
  const hasUsdPrice =
    Number.isFinite(mintUsdPrice) && mintUsdPrice > 0;

  const selectedBalance = selectedOption?.balance ?? null;

  const selectedBalanceNum =
    selectedBalance != null ? Number(selectedBalance) : null;

  const selectedBalanceUsd = useMemo(() => {
    if (selectedBalanceNum == null || !hasUsdPrice) return null;
    return selectedBalanceNum * mintUsdPrice;
  }, [hasUsdPrice, mintUsdPrice, selectedBalanceNum]);

  const withdrawAmountNum = parseFloat(withdrawAmount);
  const withdrawUsdEstimate =
    hasUsdPrice && Number.isFinite(withdrawAmountNum) && withdrawAmountNum > 0
      ? withdrawAmountNum * mintUsdPrice
      : null;

  const syncUsdFromCrypto = useCallback(
    (cryptoHuman: string) => {
      if (!hasUsdPrice || !selectedMint) {
        setWithdrawAmountUsd('0');
        return;
      }
      const crypto = parseFloat(cryptoHuman);
      if (!Number.isFinite(crypto) || crypto <= 0) {
        setWithdrawAmountUsd('0');
        return;
      }
      setWithdrawAmountUsd((crypto * mintUsdPrice).toFixed(2));
    },
    [hasUsdPrice, mintUsdPrice, selectedMint],
  );

  // The figures the withdraw mutation checks: it requires the native network
  // fee reserve on top of the amount, so Max fills balance minus that reserve.
  const [{ data: withdrawableData, fetching: withdrawableFetching }] = useWithdrawableBalanceQuery({
    variables: { organizationId, purpose, mintId },
    pause: !open || !organizationId || !mintId,
    requestPolicy: 'network-only',
  });
  // Ignore the previous mint's figures while the new ones load.
  const withdrawable = withdrawableFetching ? undefined : withdrawableData?.withdrawableBalance;
  const networkFeeReserve =
    withdrawable != null && Number(withdrawable.networkFeeReserve) > 0
      ? withdrawable.networkFeeReserve
      : null;
  const balanceBelowNetworkFee =
    networkFeeReserve != null && Number(withdrawable?.maxWithdrawable) === 0;
  const withdrawAmountAtomic = selectedMint
    ? toAtomicAmount(withdrawAmount, selectedMint.decimals)
    : null;
  const selectedBalanceAtomic =
    selectedMint && selectedBalance != null
      ? toAtomicAmount(selectedBalance, selectedMint.decimals)
      : null;
  // Once loaded, the fee-adjusted maximum caps the amount; until then the balance does.
  const maxWithdrawableAtomic =
    selectedMint && withdrawable != null
      ? toAtomicAmount(withdrawable.maxWithdrawable, selectedMint.decimals)
      : null;
  const amountCapAtomic = maxWithdrawableAtomic ?? selectedBalanceAtomic;
  const amountAboveMaxWithdrawable =
    withdrawAmountAtomic != null &&
    maxWithdrawableAtomic != null &&
    BigInt(withdrawAmountAtomic) > BigInt(maxWithdrawableAtomic);

  const slippageValue = Number(slippageBps);
  const slippageValid =
    Number.isInteger(slippageValue) &&
    slippageValue >= MIN_SLIPPAGE_BPS &&
    slippageValue <= MAX_SLIPPAGE_BPS;

  useEffect(() => {
    const id = setTimeout(() => setDebouncedAmountAtomic(withdrawAmountAtomic), QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [withdrawAmountAtomic]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSlippageBps(slippageBps), QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [slippageBps]);
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const quoteSlippageValue = Number(debouncedSlippageBps);
  const quoteSlippageValid =
    Number.isInteger(quoteSlippageValue) &&
    quoteSlippageValue >= MIN_SLIPPAGE_BPS &&
    quoteSlippageValue <= MAX_SLIPPAGE_BPS;

  const quoteVariables: WithdrawalSwapQuoteQueryVariables | null =
    isSwap &&
    selectedDestination != null &&
    whithdrawAddressId !== 0 &&
    mintId !== 0 &&
    debouncedAmountAtomic != null &&
    debouncedAmountAtomic !== '0' &&
    quoteSlippageValid &&
    !amountAboveMaxWithdrawable
      ? {
          organizationId,
          purpose,
          mintId,
          amount: debouncedAmountAtomic,
          destinationAssetId: selectedDestination.assetId,
          whitelistId: whithdrawAddressId,
          maxSlippageBps: quoteSlippageValue,
        }
      : null;

  const [
    { data: quoteData, fetching: quoteFetching, error: quoteError, operation: quoteOperation },
    reexecuteQuote,
  ] =
    useWithdrawalSwapQuoteQuery({
      variables: quoteVariables as WithdrawalSwapQuoteQueryVariables,
      pause: !open || quoteVariables == null,
      requestPolicy: 'network-only',
    });
  const quoteKey = quoteVariables != null ? JSON.stringify(quoteVariables) : null;
  // Only trust a hook result that belongs to the current variables.
  const hookQuote =
    quoteVariables != null && JSON.stringify(quoteOperation?.variables) === quoteKey
      ? quoteData?.withdrawalSwapQuote
      : undefined;
  const refreshed = refreshedQuote?.key === quoteKey ? refreshedQuote?.quote : undefined;
  const quote =
    refreshed &&
    (!hookQuote || new Date(refreshed.deadline).getTime() >= new Date(hookQuote.deadline).getTime())
      ? refreshed
      : hookQuote;
  const quoteLive =
    quote != null &&
    !quoteFetching &&
    !quoteError &&
    new Date(quote.deadline).getTime() > nowMs &&
    debouncedAmountAtomic === withdrawAmountAtomic &&
    quoteSlippageValue === slippageValue;

  useEffect(() => setQuoteNotice(null), [quoteKey]);

  useEffect(() => {
    if (!open || quoteVariables == null) return undefined;
    const id = setInterval(() => reexecuteQuote({ requestPolicy: 'network-only' }), QUOTE_REFRESH_MS);
    return () => clearInterval(id);
  }, [open, quoteVariables == null, reexecuteQuote]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateWithdrawForm = (): boolean => {
    if (whithdrawAddressId === 0) {
      return false;
    }

    if (!selectedMint) {
      toast.error('Invalid mint selected');
      return false;
    }

    const sendAmount = toAtomicAmount(withdrawAmount, selectedMint.decimals);
    if (sendAmount == null || sendAmount === '0') {
      toast.error('Please enter a valid withdraw amount');
      return false;
    }

    if (
      withdrawAmountAtomic != null &&
      amountCapAtomic != null &&
      BigInt(withdrawAmountAtomic) > BigInt(amountCapAtomic)
    ) {
      return false;
    }

    return true;
  };

  const handleWithdrawClick = async () => {
    if (isWithdrawing) return;
    if (!validateWithdrawForm()) return;
    if (isSwap) {
      if (quoteVariables == null || !slippageValid) {
        toast.error('Choose a destination asset, amount and slippage first');
        return;
      }
      // The server re-quotes at execution; refresh here so the merchant
      // does not confirm against an expired quote.
      const displayedExpired =
        !quote || new Date(quote.deadline).getTime() - Date.now() < 10_000;
      if (displayedExpired) {
        const fresh = await client
          .query<WithdrawalSwapQuoteQuery, WithdrawalSwapQuoteQueryVariables>(
            WithdrawalSwapQuoteDocument,
            quoteVariables,
            { requestPolicy: 'network-only' }
          )
          .toPromise();
        if (fresh.error || !fresh.data) {
          toast.error(
            withdrawalSwapErrorMessage(fresh.error, 'Could not refresh the quote. Please try again.')
          );
          return;
        }
        // Never submit numbers the merchant has not seen: show the new quote
        // and require a second confirmation before asking for the 2FA code.
        const next = fresh.data.withdrawalSwapQuote;
        const same =
          !!quote &&
          new Date(quote.deadline).getTime() > Date.now() &&
          quote.quotedAmountOut === next.quotedAmountOut &&
          quote.minAmountOut === next.minAmountOut &&
          quote.withdrawalFee.amount === next.withdrawalFee.amount &&
          quote.swapFee.amount === next.swapFee.amount;
        setRefreshedQuote({ key: JSON.stringify(quoteVariables), quote: next });
        if (!same) {
          setQuoteNotice('Quote updated - review and confirm again.');
          return;
        }
      }
    }
    setTotpDialogOpen(true);
  };

  const handleWithdrawSubmit = async (totpCode: string) => {
    if (isWithdrawing) return;
    setIsWithdrawing(true);

    if (!totpCode.trim()) {
      toast.error('Enter your TOTP code');
      setIsWithdrawing(false);
      return;
    }

    if (!selectedMint) {
      toast.error('Invalid mint selected');
      setIsWithdrawing(false);
      return;
    }

    const sendAmount = toAtomicAmount(withdrawAmount, selectedMint.decimals);
    if (sendAmount == null || sendAmount === '0') {
      toast.error('Please enter a valid withdraw amount');
      setIsWithdrawing(false);
      return;
    }

    try {
      if (isSwap && (quoteVariables == null || !quote || new Date(quote.deadline).getTime() <= Date.now())) {
        // The quote went stale while the 2FA modal was open: send the merchant
        // back to review the numbers without spending the code.
        setTotpDialogOpen(false);
        setQuoteNotice('Quote expired - review and confirm again.');
        return;
      }
      const response = await withdraw({
        organizationId,
        amount: sendAmount,
        mintId: mintId,
        whitelistId: whithdrawAddressId,
        purpose,
        totpCode: totpCode.trim(),
        // Only a swap carries these; a plain withdraw must omit them entirely.
        ...(isSwap && selectedDestination
          ? { destinationAssetId: selectedDestination.assetId, maxSlippageBps: slippageValue }
          : {}),
      });

      if (response.data?.withdraw) {
        toast.success(
          isSwap ? 'Withdrawal swap submitted' : 'Withdrawal Request Sent Successfully'
        );
        setTotpDialogOpen(false);
        onSuccess?.();
        void getOrganizationWallets({ requestPolicy: 'network-only' });
        onClose();
      } else if (isSwap) {
        toast.error(withdrawalSwapErrorMessage(response.error, 'Withdrawal Failed'));
      } else {
        toast.error('Withdrawal Failed');
      }
    } catch (err) {
      const message =
        !isSwap && err instanceof Error ? err.message : 'Withdrawal Request Failed';
      toast.error(message);
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleMax = () => {
    if (withdrawable == null) {
      return;
    }
    setWithdrawAmount(withdrawable.maxWithdrawable);
    syncUsdFromCrypto(withdrawable.maxWithdrawable);
  };

  const handleCryptoAmountChange = (value: string) => {
    setWithdrawAmount(value);
    syncUsdFromCrypto(value);
  };

  const handleUsdAmountChange = (value: string) => {
    setWithdrawAmountUsd(value);
    if (!selectedMint || !hasUsdPrice) {
      return;
    }
    setWithdrawAmount(
      cryptoHumanFromUsd(value, mintUsdPrice, selectedMint.decimals),
    );
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>Withdraw Funds</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          minWidth: '400px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
            <Typography sx={{ color: 'rgba(177,186,211)', fontSize: '12px' }} variant="body2">
              Select Token
            </Typography>
            {networkPills.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 1,
                }}
              >
                <Chip
                  label="All"
                  size="small"
                  clickable
                  color={chainFilter === 'all' ? 'primary' : 'default'}
                  variant={chainFilter === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setChainFilter('all')}
                />
                {networkPills.map((network) => (
                  <Chip
                    key={network}
                    label={networkDisplayName(network)}
                    size="small"
                    clickable
                    color={chainFilter === network ? 'primary' : 'default'}
                    variant={chainFilter === network ? 'filled' : 'outlined'}
                    onClick={() => setChainFilter(network)}
                  />
                ))}
              </Box>
            )}
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: 280,
                overflowY: 'auto',
              }}
            >
              {filteredWithdrawOptions.length === 0 ? (
                <Alert severity="info">
                  No tokens with a balance to withdraw
                </Alert>
              ) : (
                filteredWithdrawOptions.map((option) => {
                  const selected = option.mintId === mintId;
                  const iconUrl = mintTokenIconUrl({
                    img: option.img,
                    chain: option.chain,
                  });
                  return (
                    <Box
                      key={option.mintId}
                      role="button"
                      tabIndex={0}
                      onClick={() => selectWithdrawToken(option)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          selectWithdrawToken(option);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        '&:hover': {
                          bgcolor: selected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        {iconUrl ? (
                          <Box
                            component="img"
                            src={iconUrl}
                            alt={option.symbol}
                            width={32}
                            height={32}
                            sx={{ borderRadius: '50%', flexShrink: 0 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: 'action.disabledBackground',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Box sx={{ ml: 1.25, minWidth: 0 }}>
                          <Typography variant="body1" noWrap>
                            {option.symbol}
                          </Typography>
                          <Typography
                            sx={{ color: 'rgba(177,186,211)', fontSize: '12px' }}
                            variant="body2"
                            noWrap
                          >
                            {Number(option.balance).toLocaleString()} {option.symbol}
                            {' · '}
                            {networkDisplayName(option.chain)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ flexShrink: 0 }}
                      >
                        {fmtUSD(option.usdBalance)}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
          {isSwapSource && (
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
              <Typography sx={{ color: 'rgba(177,186,211)', fontSize: '12px' }} variant="body2">
                Receive as
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Select
                  value={receiveAs}
                  onChange={(e) => {
                    setReceiveAs(e.target.value as 'same' | 'other');
                    setDestinationAssetId(null);
                  }}
                >
                  <MenuItem value="same">Same asset</MenuItem>
                  <MenuItem value="other">Another asset (swap)</MenuItem>
                </Select>
              </FormControl>
              {isSwap && (
                <>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <Select
                      displayEmpty
                      value={destinationChain}
                      onChange={(e) => {
                        setDestinationChain(e.target.value as Chain);
                        setDestinationAssetId(null);
                      }}
                      renderValue={(value) => (value ? value : 'Destination chain')}
                    >
                      {swapChainOptions.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Autocomplete
                    sx={{ mt: 1 }}
                    disabled={!destinationChain}
                    loading={destinationsFetching}
                    options={destinationOptions}
                    value={selectedDestination}
                    onChange={(_e, option) => setDestinationAssetId(option?.assetId ?? null)}
                    getOptionLabel={(option) => `${option.symbol} (${option.chain})`}
                    isOptionEqualToValue={(a, b) => a.assetId === b.assetId}
                    renderInput={(params) => (
                      <TextField {...params} label="Destination asset" size="small" />
                    )}
                  />
                </>
              )}
            </Box>
          )}
          {chain === 'ETHEREUM' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                mt: 2,
              }}
            >
              <Typography sx={{ color: 'rgba(177,186,211)', fontSize: '12px' }} variant="body2">
                Network
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Select value={chain} disabled sx={{ width: '100%', height: '60px' }}>
                  <MenuItem value="ETHEREUM">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <svg
                        width={20}
                        height={20}
                        fill="none"
                        viewBox="0 0 96 96"
                        className="svg-icon"
                      >
                        <path
                          d="M96 48c0 26.51-21.49 48-48 48S0 74.51 0 48 21.49 0 48 0s48 21.49 48 48Z"
                          fill="#627EEA"
                        />
                        <path opacity=".6" d="M49.5 12v26.61L72 48.66 49.5 12Z" fill="#fff" />
                        <path d="M49.5 12 27 48.66l22.5-10.05V12Z" fill="#fff" />
                        <path opacity=".6" d="M49.5 65.91V84L72 52.845 49.5 65.91Z" fill="#fff" />
                        <path d="M49.5 84V65.895L27 52.845 49.5 84Z" fill="#fff" />
                        <path
                          opacity=".2"
                          d="M49.5 61.725 72 48.66 49.5 38.61v23.115Z"
                          fill="#fff"
                        />
                        <path
                          opacity=".6"
                          d="m27 48.66 22.5 13.065V38.61L27 48.66Z"
                          fill="#fff"
                        />
                      </svg>
                      <Box
                        sx={{
                          ml: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography variant="body1">ETH</Typography>
                        <Typography
                          sx={{ color: 'rgba(177,186,211)', fontSize: '12px' }}
                          variant="body2"
                        >
                          Ethereum
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
            <Typography sx={{ color: 'rgba(177,186,211)', fontSize: '12px' }} variant="body2">
              Withdraw Address
            </Typography>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select
                sx={{ width: '100%', height: '60px' }}
                value={whitelistAddressByChain?.length === 0 ? 0 : whithdrawAddressId}
                disabled={!whitelistAddressByChain?.length}
                onChange={(e) => {
                  setWithdrawAddressId(e.target.value as number);
                }}
              >
                {whitelistAddressByChain &&
                  whitelistAddressByChain.map((address) => (
                    <MenuItem key={address.address} value={address.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                        <Typography variant="body1">{address.address}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                {whitelistAddressByChain && whitelistAddressByChain.length === 0 && (
                  <MenuItem value={0}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1">No Whitelisted Addresses Found</Typography>
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            {whitelistAddressByChain?.length === 0 && !(isSwap && !destinationChain) && (
              <Alert severity="error" sx={{ maxWidth: '350px', mt: 2 }}>
                You have no whitelisted addresses for this chain. Please contact admin!
                {isSwap && (
                  <>
                    {' '}
                    <Link component={RouterLink} to={paths.dashboard.organization} onClick={onClose}>
                      View whitelisted addresses
                    </Link>
                  </>
                )}
              </Alert>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{ my: 'auto', color: 'rgba(177,186,211)', fontSize: '12px' }}
                variant="body2"
              >
                Amount to Withdraw
              </Typography>
              {mintSymbol && selectedBalance != null && (
                <Typography
                  sx={{ my: 'auto', color: 'rgba(177,186,211)', fontSize: '12px' }}
                  variant="body2"
                >
                  Balance: {Number(selectedBalance).toLocaleString()} {mintSymbol}
                  {selectedBalanceUsd != null
                    ? ` (${fmtUSD(selectedBalanceUsd)})`
                    : ''}
                </Typography>
              )}
            </Box>
            {hasUsdPrice ? (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={amountMode}
                onChange={(_, next: WithdrawAmountMode | null) => {
                  if (next == null) {
                    return;
                  }
                  setAmountMode(next);
                  if (next === 'usd') {
                    syncUsdFromCrypto(withdrawAmount);
                  }
                }}
                sx={{ mt: 1 }}
              >
                <ToggleButton value="crypto">{mintSymbol ?? 'Token'}</ToggleButton>
                <ToggleButton value="usd">USD</ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            <OutlinedInput
              endAdornment={
                <Typography>
                  {amountMode === 'usd' ? 'USD' : selectedMint?.symbol}
                </Typography>
              }
              sx={{ mt: 1, width: '100%', height: '40px' }}
              placeholder="Enter Amount"
              type="number"
              value={amountMode === 'usd' ? withdrawAmountUsd : withdrawAmount}
              onChange={(e) => {
                if (amountMode === 'usd') {
                  handleUsdAmountChange(e.target.value);
                } else {
                  handleCryptoAmountChange(e.target.value);
                }
              }}
            />
            {hasUsdPrice && amountMode === 'crypto' && withdrawUsdEstimate != null ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                ≈ {fmtUSD(withdrawUsdEstimate)}
              </Typography>
            ) : null}
            {hasUsdPrice &&
            amountMode === 'usd' &&
            mintSymbol &&
            Number.isFinite(withdrawAmountNum) &&
            withdrawAmountNum > 0 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                ≈ {withdrawAmount} {mintSymbol}
              </Typography>
            ) : null}
            <Button
              onClick={handleMax}
              variant="contained"
              sx={{ mt: 1 }}
              disabled={
                selectedBalanceNum == null ||
                !Number.isFinite(selectedBalanceNum) ||
                withdrawable == null
              }
            >
              Max
            </Button>
            {(balanceBelowNetworkFee || amountAboveMaxWithdrawable) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {balanceBelowNetworkFee
                  ? 'Balance is too low to withdraw'
                  : `Amount exceeds the maximum withdrawable (${withdrawable?.maxWithdrawable} ${mintSymbol})`}
              </Alert>
            )}
            {isSwap && (
              <>
                {quoteNotice && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {quoteNotice}
                  </Alert>
                )}
                <WithdrawalSwapQuotePanel
                  quote={quote}
                  fetching={quoteFetching}
                  errorMessage={
                    quoteVariables != null && quoteError
                      ? withdrawalSwapErrorMessage(quoteError, 'Could not get a quote. Please try again.')
                      : null
                  }
                />
                <Accordion disableGutters elevation={0} sx={{ mt: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary>
                    <Typography variant="body2">Advanced</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      label="Max slippage (bps)"
                      type="number"
                      size="small"
                      fullWidth
                      value={slippageBps}
                      onChange={(e) => setSlippageBps(e.target.value)}
                      error={!slippageValid}
                      helperText={
                        slippageValid
                          ? `${(slippageValue / 100).toFixed(2)}%`
                          : `Enter ${MIN_SLIPPAGE_BPS} to ${MAX_SLIPPAGE_BPS} (default ${DEFAULT_SLIPPAGE_BPS})`
                      }
                    />
                  </AccordionDetails>
                </Accordion>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Withdrawals above your organization&apos;s approval threshold are held for admin
                  approval and quoted again when approved, so the final amount may differ from this
                  quote (never below your slippage limit).
                </Alert>
              </>
            )}
          </Box>
        </Box>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => void handleWithdrawClick()}
            color="primary"
            disabled={
              mintId === 0 ||
              filteredWithdrawOptions.length === 0 ||
              (isSwap && (quoteVariables == null || !selectedDestination || !quoteLive)) ||
              (whitelistAddressByChain?.length ?? 0) === 0 ||
              isWithdrawing ||
              withdrawAmount.trim() === '' ||
              withdrawAmountAtomic === '0' ||
              (withdrawAmountAtomic != null &&
                amountCapAtomic != null &&
                BigInt(withdrawAmountAtomic) > BigInt(amountCapAtomic))
            }
          >
            Withdraw
          </Button>
        </DialogActions>
      </DialogContent>
      <WithdrawTotpDialog
        open={totpDialogOpen}
        loading={isWithdrawing}
        onClose={() => {
          if (!isWithdrawing) {
            setTotpDialogOpen(false);
          }
        }}
        onSubmit={(code) => void handleWithdrawSubmit(code)}
      />
    </Dialog>
  );
}
